import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { queryKeys } from './queryKeys'
import { ApiRequestError } from '@/lib/httpClient'
import { permissions } from '@/types/authorization'
import {
  deleteAllocation,
  fetchAllocationCandidates,
  fetchAllocationRecommendations,
  fetchAllocations,
  fetchCapacityData,
  fetchCapacitySettings,
  fetchSourceAllocationStaff,
  fetchStaffAvailability,
  fetchWorkflowJobs,
  removeSourceAllocationStaff,
  replaceJobAllocations,
  saveAllocations,
  saveCapacitySettings,
  saveJobBudget,
  saveJobBudgets,
  validateSourceAllocationStaffRemoval,
} from './api'
import type { RemoveSourceAllocationStaffPayload, ReplaceJobAllocationsPayload, SaveAllocationPayload, SaveAllocationsOptions, SaveJobBudgetPayload, SaveSettingsPayload, ValidateSourceAllocationStaffRemovalPayload } from './types'

function isUnauthorizedApiError(error: unknown) {
  return error instanceof ApiRequestError && error.status === 401
}

function invalidateCapacityPlannerDataQueries(queryClient: QueryClient, clientId: string) {
  queryClient.invalidateQueries({ queryKey: ['capacity-planner', 'capacity', clientId] })
  queryClient.invalidateQueries({ queryKey: ['capacity-planner', 'workflow', clientId] })
  queryClient.invalidateQueries({ queryKey: ['capacity-planner', 'allocation-candidates', clientId] })
  queryClient.invalidateQueries({ queryKey: ['capacity-planner', 'allocations', clientId] })
  queryClient.invalidateQueries({ queryKey: ['capacity-planner', 'staff-availability', clientId] })
  queryClient.invalidateQueries({ queryKey: ['capacity-planner', 'source-allocation-staff', clientId] })
  queryClient.invalidateQueries({ queryKey: ['capacity-planner', 'allocation-recommendations', clientId] })
}

/**
 * A 401 here is never the user's problem to see: the request layer is already
 * refreshing the token or redirecting to login. Surfacing an error card in the
 * moment before that happens just flashes a failure the user cannot act on, so
 * an unauthorized response keeps the caller in its loading state instead.
 */
export function useCapacitySettings(clientId: string, options: { enabled?: boolean } = {}) {
  const authorization = useAuthorization()
  const isEnabled =
    !!clientId &&
    (options.enabled ?? true) &&
    authorization.isReady &&
    authorization.hasPermission(permissions.viewCapacityPlanner)

  const query = useQuery({
    queryKey: queryKeys.settings(clientId),
    queryFn: () => fetchCapacitySettings(clientId),
    enabled: isEnabled,
    retry: (failureCount, error) => !isUnauthorizedApiError(error) && failureCount < 3,
  })

  const isUnauthorized = query.isError && isUnauthorizedApiError(query.error)

  // `status` is overridden alongside the booleans: leaving it as 'error' while
  // reporting isError false gives callers two answers to the same question, and
  // react-query's own narrowing keys off status rather than the flags.
  return {
    ...query,
    status: isUnauthorized ? ('pending' as const) : query.status,
    isLoading: query.isLoading || isUnauthorized,
    isPending: query.isPending || isUnauthorized,
    isError: query.isError && !isUnauthorized,
    error: isUnauthorized ? null : query.error,
  }
}

export function useSaveCapacitySettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, payload }: { clientId: string; payload: SaveSettingsPayload }) =>
      saveCapacitySettings(clientId, payload),
    onSuccess: async (settings, { clientId }) => {
      queryClient.setQueryData(queryKeys.settings(clientId), settings)
      queryClient.removeQueries({ queryKey: ['capacity-planner', 'capacity', clientId] })
      queryClient.removeQueries({ queryKey: ['capacity-planner', 'workflow', clientId] })
      queryClient.removeQueries({ queryKey: ['capacity-planner', 'allocation-candidates', clientId] })
      queryClient.removeQueries({ queryKey: ['capacity-planner', 'allocations', clientId] })
      queryClient.removeQueries({ queryKey: ['capacity-planner', 'staff-availability', clientId] })
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings(clientId) })
    },
  })
}

export function useCapacityData(
  clientId: string,
  startDate: string,
  endDate: string,
  options?: { team?: string; search?: string; weekLabel?: string; periodStart?: string; page?: number; pageSize?: number }
) {
  return useQuery({
    queryKey: queryKeys.capacity(clientId, startDate, endDate, options),
    queryFn: () => fetchCapacityData(clientId, startDate, endDate, options),
    enabled: !!clientId && !!startDate && !!endDate,
    placeholderData: (previousData) => previousData,
  })
}

export function useWorkflowJobs(
  clientId: string,
  options?: { staffId?: string; state?: string; search?: string; page?: number; pageSize?: number }
) {
  return useQuery({
    queryKey: queryKeys.workflow(clientId, options),
    queryFn: () => fetchWorkflowJobs(clientId, options),
    enabled: !!clientId,
  })
}

export function useAllocationCandidates(
  clientId: string,
  options?: { allocationStatus?: string; state?: string; search?: string; page?: number; pageSize?: number }
) {
  return useQuery({
    queryKey: queryKeys.allocationCandidates(clientId, options),
    queryFn: () => fetchAllocationCandidates(clientId, options),
    enabled: !!clientId,
  })
}

export function useSourceAllocationStaff(clientId: string, jobId: string) {
  return useQuery({
    queryKey: queryKeys.sourceAllocationStaff(clientId, jobId),
    queryFn: () => fetchSourceAllocationStaff(clientId, jobId),
    enabled: !!clientId && !!jobId,
    retry: false,
  })
}

export function useAllocations(
  clientId: string,
  weekStart: string,
  weekEnd: string,
  options?: { search?: string; staffId?: string; page?: number; pageSize?: number }
) {
  return useQuery({
    queryKey: queryKeys.allocations(clientId, weekStart, weekEnd, options),
    queryFn: () => fetchAllocations(clientId, weekStart, weekEnd, options),
    enabled: !!clientId && !!weekStart && !!weekEnd,
  })
}

export function useStaffAvailability(
  clientId: string,
  weekStart: string,
  weekEnd: string,
  options?: { search?: string; staffId?: string; page?: number; pageSize?: number }
) {
  return useQuery({
    queryKey: queryKeys.staffAvailability(clientId, weekStart, weekEnd, options),
    queryFn: () => fetchStaffAvailability(clientId, weekStart, weekEnd, options),
    enabled: !!clientId && !!weekStart && !!weekEnd,
  })
}

export function useAllocationRecommendations(clientId: string, jobIds: string[], weekStart: string, weekCount: number) {
  return useQuery({
    queryKey: queryKeys.allocationRecommendations(clientId, jobIds, weekStart, weekCount),
    queryFn: () => fetchAllocationRecommendations(clientId, jobIds, weekStart, weekCount),
    enabled: !!clientId && jobIds.length > 0 && !!weekStart,
    staleTime: 60_000,
  })
}

export function useSaveAllocations() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, allocations, options }: { clientId: string; allocations: SaveAllocationPayload[]; options?: SaveAllocationsOptions }) =>
      saveAllocations(clientId, allocations, options),
    onSuccess: (_, { clientId }) => {
      invalidateCapacityPlannerDataQueries(queryClient, clientId)
    },
  })
}

export function useReplaceJobAllocations() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, payload }: { clientId: string; payload: ReplaceJobAllocationsPayload }) =>
      replaceJobAllocations(clientId, payload),
    onSuccess: (_, { clientId }) => {
      invalidateCapacityPlannerDataQueries(queryClient, clientId)
    },
  })
}

export function useSaveJobBudget() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, budget }: { clientId: string; budget: SaveJobBudgetPayload }) =>
      saveJobBudget(clientId, budget),
    onSuccess: (_, { clientId }) => {
      invalidateCapacityPlannerDataQueries(queryClient, clientId)
    },
  })
}

export function useSaveJobBudgets() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, budgets }: { clientId: string; budgets: SaveJobBudgetPayload[] }) =>
      saveJobBudgets(clientId, budgets),
    onSuccess: (_, { clientId }) => {
      invalidateCapacityPlannerDataQueries(queryClient, clientId)
    },
  })
}

export function useDeleteAllocation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ allocationId }: { allocationId: string; clientId: string }) => deleteAllocation(allocationId),
    onSuccess: (_, { clientId }) => {
      invalidateCapacityPlannerDataQueries(queryClient, clientId)
    },
  })
}

export function useRemoveSourceAllocationStaff() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, payload }: { clientId: string; payload: RemoveSourceAllocationStaffPayload }) =>
      removeSourceAllocationStaff(clientId, payload),
    onSuccess: (_, { clientId }) => {
      invalidateCapacityPlannerDataQueries(queryClient, clientId)
    },
  })
}

export function useValidateSourceAllocationStaffRemoval() {
  return useMutation({
    mutationFn: ({ clientId, payload }: { clientId: string; payload: ValidateSourceAllocationStaffRemovalPayload }) =>
      validateSourceAllocationStaffRemoval(clientId, payload),
  })
}
